/**
 * NOTIFICATION-CENTER-UX-D1 — visual unread badge contract.
 *
 * Canonical Redux/server unread count remains uncapped.
 * Visual badge alone is capped; a11y exact count belongs on the parent link.
 *
 * 0      → hidden (null display)
 * 1..99  → exact number
 * >=100  → "99+"
 */

export function normalizeUnreadCount(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

export function formatUnreadBadgeDisplay(count) {
  const n = normalizeUnreadCount(count);
  if (n <= 0) return null;
  if (n > 99) return "99+";
  return String(n);
}

export function formatUnreadBadgeAriaLabel(count) {
  const n = normalizeUnreadCount(count);
  if (n <= 0) return null;

  const absMod100 = n % 100;
  const absMod10 = n % 10;
  let nounPhrase;
  if (absMod100 > 10 && absMod100 < 20) {
    nounPhrase = "непрочитаних сповіщень";
  } else if (absMod10 === 1) {
    nounPhrase = "непрочитане сповіщення";
  } else if (absMod10 >= 2 && absMod10 <= 4) {
    nounPhrase = "непрочитані сповіщення";
  } else {
    nounPhrase = "непрочитаних сповіщень";
  }
  return `${n} ${nounPhrase}`;
}

/**
 * Parent navigation link accessible name for notifications.
 * Example: `Сповіщення: 1203 непрочитані сповіщення`
 */
export function formatNotificationNavAccessibleName(itemLabel, count) {
  const label =
    typeof itemLabel === "string" && itemLabel.trim()
      ? itemLabel.trim()
      : "Сповіщення";
  const countLabel = formatUnreadBadgeAriaLabel(count);
  if (!countLabel) return label;
  return `${label}: ${countLabel}`;
}

/**
 * Shared render contract for nav badges.
 * Returns null when the badge should be hidden.
 */
export function resolveUnreadBadge(count) {
  const n = normalizeUnreadCount(count);
  const display = formatUnreadBadgeDisplay(n);
  if (!display) return null;
  return {
    count: n,
    display,
    ariaLabel: formatUnreadBadgeAriaLabel(n),
  };
}

/**
 * Notification-nav presentation: exact count on the link, decorative badge.
 */
export function resolveNotificationNavPresentation(itemLabel, count) {
  const badge = resolveUnreadBadge(count);
  return {
    linkAriaLabel: formatNotificationNavAccessibleName(itemLabel, count),
    badge,
  };
}
